'use client';

/**
 * @brisa/ui · components/ui/switch — shadcn Switch (Facelift v1)
 *
 * Feature: migracao-facelift-v1 · ADR §D6 · adicionado Bruno 2026-05-16
 *
 * Wrapper Radix UI switch com estilo facelift, derivado do toggle
 * "aceita Brisa Card" na ClubesPage (Figma node 194:3943 → componente
 * Switch node 194:6075 / 194:6091).
 *
 * Specs Figma (componente canônico do design system):
 *   - Track: 36×20 (w-9 h-5), border-radius full, padding 2px lateral
 *   - Thumb: 16×16 (h-4 w-4), branco, drop shadow, border-radius full
 *   - Translate ON: 16px (data-[state=checked]:translate-x-4)
 *   - Track OFF: cinza claro (#E5E5E5 → hsl(var(--text-08)) aprox.)
 *     [shadcn default `bg-input` mapeia para --input = text-16, coerente]
 *   - Track ON: preto sólido (#171717 — Figma var "switch/on")
 *     → hsl(var(--text-100)) (preto puro do nosso token system)
 *   - Transição: 200ms ease-out — duration-base padrão
 *
 * Figma origem: fileKey 4kRRaXDq85zVVXByHGEFfY · node 194:3943 (/clubes)
 *               componente Switch node 194:6075 (On) / 194:6091 (Off)
 *
 * Decisão Facelift v1: a variant "Type=Icon" do Figma (thumb com ícone
 * embutido) NÃO é implementada nesta versão — uso atual na /clubes é
 * Switch padrão sem ícone. Adicionar quando algum consumer pedir.
 *
 * 'use client' obrigatório — Radix Switch orquestra state interno.
 */
import * as React from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';

import { cn } from '../../lib/utils';

export const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>
>(({ className, ...props }, ref) => (
  <SwitchPrimitive.Root
    ref={ref}
    className={cn(
      // Track base — 36×20, pill, padding 2px lateral via items-center + sizing thumb
      'peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent',
      // Transição suave (200ms ease-out per --duration-base)
      'transition-colors duration-base ease-out',
      // Cores — OFF cinza claro (text-08 aprox.) / ON preto sólido (text-100)
      'data-[state=unchecked]:bg-[hsl(var(--text-16))]',
      'data-[state=checked]:bg-[hsl(var(--text-100))]',
      // Acessibilidade — focus ring amarelo + disabled
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      'disabled:cursor-not-allowed disabled:pointer-events-none disabled:opacity-50',
      className,
    )}
    {...props}
  >
    <SwitchPrimitive.Thumb
      className={cn(
        // Thumb 16×16 branco com shadow (Figma var "switch/thumb")
        'pointer-events-none block h-4 w-4 rounded-full bg-[hsl(var(--neutral-white))] shadow-sm ring-0',
        // Transição transform 200ms ease-out — slide horizontal
        'transition-transform duration-base ease-out',
        // OFF → translateX(0); ON → translateX(16px = 1rem)
        'data-[state=unchecked]:translate-x-0 data-[state=checked]:translate-x-4',
      )}
    />
  </SwitchPrimitive.Root>
));
Switch.displayName = SwitchPrimitive.Root.displayName;
